import { ISinglePin } from "src/shared/types";
import { abbreviateNumber, parseJson, safeGet } from "src/shared/utils";
import { getRoundOffTimeKey } from "src/shared/utils/timeUtils";
import { FEATURE_FLAG, GRANULARITY_MAPPING } from "src/shared/config";
import moment from "moment";
import { IsTabHidden } from "src/shared/utils/customTab";
import { getServiceName, ServiceName } from "src/utils/getservicename";
import { devLogging } from "src/utils/logger";

interface IRequest {
  metric_id: number;
  slice_id: number;
  filters: {
    adhoc_filters: any[];
    chronic_filters: Record<string, unknown>;
  };
}

export enum PageTypes {
  L1 = "L1",
  L2 = "L2",
  L3 = "L3",
  L1Search = "L1Search",
  L2Search = "L2Search",
}
const Today = "Today",
  SDLW = "SDLW",
  Yesterday = "Yesterday",
  Daily_Trend = "Daily Trend",
  _1_Week_Ago = "1 Week Ago",
  _4_Weeks_Ago = "4 Weeks Ago";
export const chartLegendOffsets = {
  Today: { current: Today, past: SDLW },
  Yesterday: { current: Yesterday, past: SDLW },
  "Last 7 days": { current: Daily_Trend, past: _1_Week_Ago },
  "Last 4 weeks": { current: Daily_Trend, past: _4_Weeks_Ago },
  Today_imcom: { current: Today, past: Today },
  CurrHr_imcom: { current: "Current Hour", past: "Current Hour" },
  LastHr_imcom: { current: "Last Hour", past: "Last Hour" },
  Yesterday_imcom: { current: Yesterday, past: Yesterday },
  Today_india_2: { current: Today, past: SDLW },
  Yesterday_india_2: { current: Yesterday, past: SDLW },
  SDLW_india_2: { current: Daily_Trend, past: _4_Weeks_Ago },
};

export type timerange = keyof typeof chartLegendOffsets;

const labels = [Today, Yesterday, Daily_Trend, _1_Week_Ago, _4_Weeks_Ago] as const;

type TrendChartData = {
  [key in typeof labels[number]]: number;
} & {
  x: number;
  formatted: string;
};

export const chartsToFetch: { [key in PageTypes]: Array<string> } = {
  [PageTypes.L1]: ["currentChart", "pastChart", "trendChart"],
  [PageTypes.L2]: FEATURE_FLAG.LINE_CHART_FOR_CATEGORY_PAGE
    ? ["currentChart", "pastChart", "trendChart"]
    : ["currentChart", "pastChart"],
  [PageTypes.L3]: ["currentChartId", "pastChartId", "trendChartId", "planOrderLineChartId"],
  [PageTypes.L1Search]: ["currentChart", "pastChart"],
  [PageTypes.L2Search]: ["currentChart", "pastChart"],
};

const week = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function getDayOfWeek(timestamp: number) {
  let dayOfWeek = new Date(timestamp).getDay();
  return week[dayOfWeek];
}

export function formatDate(timestamp: number, granularity: string) {
  const timeZoneOffset = 19800000; // to offset time by 5:30 hours
  switch (granularity) {
    case "1 hour": {
      return `${new Date(timestamp - timeZoneOffset).getHours()}:${(
        "0" + new Date(timestamp - timeZoneOffset).getMinutes()
      ).slice(-2)}`;
    }
    case "1 days": {
      return `${new Date(timestamp - timeZoneOffset).getDate()}/${(
        "0" +
        (new Date(timestamp - timeZoneOffset).getMonth() + 1)
      ).slice(-2)}`;
    }
    case "1 day": {
      return getDayOfWeek(timestamp - timeZoneOffset);
    }
    default:
      return new Date(timestamp - timeZoneOffset).toString();
  }
}

export default class MetricOperations {
  public static comparePrefix = {
    Today: "% WOW",
    Yesterday: "% WOW",
    "Last 7 days": "% change",
    "Last 4 weeks": "% change",
    Today_imcom: " Pods",
    CurrHr_imcom: " Pods",
    LastHr_imcom: " Pods",
    Yesterday_imcom: " Pods",
    Today_india_2: " Restaurants",
    Yesterday_india_2: " Restaurants",
    SDLW_india_2: " Restaurants",
  };
  public static comparePrefixPercent = {
    Today: "% pts WOW",
    Yesterday: "% pts WOW",
    "Last 7 days": "% pts change",
    "Last 4 weeks": "% pts change",
    Today_imcom: " Pods",
    CurrHr_imcom: " Pods",
    LastHr_imcom: " Pods",
    Yesterday_imcom: " Pods",
    Today_india_2: " Restaurants",
    Yesterday_india_2: " Restaurants",
    SDLW_india_2: " Restaurants",
  };

  public static getBulkChartRequest = (
    chronic,
    pins: Array<any>,
    type: string,
    metricType: string,
    adhocFilters?,
    filterConfig?
  ) => {
    const charts = chartsToFetch[type];
    const reducerOuter = (accumulatorOuter, currentValueOuter) => {
      const reducer = (accumulator, currentValue) => {
        const metricId = currentValueOuter.metricId || 0;
        const pinId = currentValueOuter.id;
        const sliceId = safeGet(currentValueOuter, currentValue, 0) as number;
        
        // Skip if slice_id is not available (e.g., planOrderLineChartId not returned by API)
        if (!sliceId) {
          return accumulator;
        }
        
        const a = accumulator.concat({
          ...(metricType === "pin" && { pin_id: pinId }),
          metric_id: metricId,
          slice_id: sliceId,
          time_view: currentValue,
          filters: {
            adhoc_filters: adhocFilters || currentValueOuter.filters || [],
            chronic_filters:
              currentValue.indexOf("pastChart") >= 0
                ? // currentValue === "pastChart" || currentValue === "pastChartId"
                  MetricOperations.parseChronicFilter(filterConfig, chronic, "pastChart")
                : currentValue.indexOf("trendChart") >= 0 || currentValue.indexOf("planOrderLineChart") >= 0
                ? // trendChart or planOrderLineChart - both use trend filter with time_compare
                  MetricOperations.parseChronicFilter(filterConfig, chronic, "trendChart")
                : MetricOperations.parseChronicFilter(filterConfig, chronic, "currentChart"),
          },
        });
        return a;
      };
      return accumulatorOuter.concat(charts.reduce(reducer, []));
    };
    return JSON.stringify(pins.reduce(reducerOuter, []));
  };

  public static combineMetricsAndCharts = (chartsResponse, pinsResponse, type: PageTypes) => {
    const charts = chartsToFetch[type];
    const result: any[] = [];
    safeGet(pinsResponse, "data", []).forEach((element) => {
      const combined = { ...element };
      charts.forEach((chart) => {
        let chartData;
        if (type === PageTypes.L1) {
          chartData = chartsResponse.find(
            ({ pinId, slice_id, time_view }) =>
              (pinId ? pinId === element.id : true) && slice_id === safeGet(element, chart, 0) && time_view === chart
          );
        } else {
          chartData = chartsResponse.find(
            ({ metric_id, slice_id, time_view }) =>
              metric_id === element.metricId && slice_id === safeGet(element, chart, 0) && time_view === chart
          );
        }
        if (chartData) {
          combined.isStaleMetric =
            !combined.isStaleMetric && !chart.startsWith("pastChart")
              ? chartData.isStaleMetric
              : combined.isStaleMetric;
          combined[chart] = chartData;
        }
      });
      result.push(combined);
    });
    if (type === PageTypes.L1) return { ...pinsResponse, data: result };
    else return result;
  };

  public static calculateChartsData = (pin: ISinglePin, timeTab: string) => {
    const newPin = {
      ...pin,
      hasChart: false,
      lastUpdatedTimeStamp: moment.utc(pin.currentChart.lastUpdatedTimeStamp).format("h:mm A"),
    };
    const currentchartDataPoints = safeGet(pin, "currentChart.data.data.data", {});
    const bigNumber = safeGet(currentchartDataPoints, "[0][0]", 0);
    const pastChartDataPoints = safeGet(pin, "pastChart.data.data.data", {});
    const pastBigNumber = safeGet(pastChartDataPoints, "[0][0]");

    let vCompare = pin.isPercentage
      ? bigNumber - pastBigNumber
      : pastBigNumber === 0
      ? 100
      : ((bigNumber - pastBigNumber) * 100) / pastBigNumber;

    devLogging("Here is the time tab for Anobis", timeTab);
    // condition to check IMCoM App
    if (getServiceName() === ServiceName.ANOBIS_IMCOM) {
      vCompare = pastBigNumber;
    }

    // condition to check India2MIS App
    if (getServiceName() === ServiceName.ANOBIS_INDIA_2) {
      vCompare = pastBigNumber;
    }

    const progress = vCompare > 0;
    const cPrefix = pin.isPercentage ? MetricOperations.comparePrefixPercent : MetricOperations.comparePrefix;
    newPin.bigNumber = !IsTabHidden(pin.isHidden, timeTab) ? abbreviateNumber(bigNumber) : "-";
    const isPastNumberPresent = safeGet(pastChartDataPoints, "[0][0]", false) !== false;
    newPin.vCompare = isPastNumberPresent ? abbreviateNumber(vCompare).concat(cPrefix[timeTab] || "") : "";
    newPin.progress = progress;

    newPin.bigNumber = pin.unit
      ? pin.unit === "INR"
        ? "₹" + newPin.bigNumber
        : newPin.bigNumber.concat(pin.unit)
      : newPin.bigNumber;

    newPin.successful = safeGet(currentchartDataPoints, "[0][0]", false) !== false;
    newPin.hasChart = true;
    newPin.isAnomalous = vCompare < newPin.lowerBound || vCompare > newPin.upperBound ? 2 : newPin.isAnomalous ? 1 : 0;
    return newPin;
  };

  public static calculationsFromCharts = (metricArray: any, timeTab: string) => {
    const newArray = safeGet(metricArray, "data", []).map((element) => {
      return MetricOperations.calculateChartsData(element, timeTab);
    });
    return { ...metricArray, data: newArray };
  };
  public static formatCharts(formsData) {
    return formsData.map((item) => {
      return {
        pinId: item.pin_id,
        metric_id: item.metric_id,
        slice_id: item.slice_id,
        filters: item.filters,
        data: parseJson(item.data),
        time_view: item.time_view,
        isStaleMetric: item.isStaleMetric,
        lastUpdatedTimeStamp: item.lastUpdatedTimeStamp,
      };
    });
  }
  public static parseChronicFilter(filterConfig, tabName, chartType: "currentChart" | "pastChart" | "trendChart") {
    const x = filterConfig.find((element) => element.tabName === tabName && element.chartType === chartType);
    return {
      from: x.from,
      to: x.to,
      granularity: x.granularity,
      time_compare: [x.timeCompare],
    };
  }

  public static parseTrendChartData(
    isHidden,
    timetab,
    currTrendChartData: any,
    pastTrendChartData: any,
    timerange: timerange,
    planOrderLineChartData?: any
  ) {
    const currDataParsed = !IsTabHidden(isHidden, timetab) ? parseJson(safeGet(currTrendChartData, "data", {})) : {};
    const currData: Array<IObject<any>> = safeGet(currDataParsed, "data");

    const pastDataParsed = !IsTabHidden(isHidden, timetab) ? parseJson(safeGet(pastTrendChartData, "data", {})) : {};
    const pastData: Array<IObject<any>> = safeGet(pastDataParsed, "data");

    // Parse plan order line chart data if available
    const planDataParsed = planOrderLineChartData && !IsTabHidden(isHidden, timetab)
      ? parseJson(safeGet(planOrderLineChartData, "data", {}))
      : {};
    const planData: Array<IObject<any>> = safeGet(planDataParsed, "data");

    const granularity = GRANULARITY_MAPPING[timerange];
    const PLAN_ORDERS_KEY = "Plan Orders";

    const res = {};
    type chart = { key: string; values: { x: number; y: number }[] };
    if (safeGet(currData, "length", 0) > 0) {
      const chart1: chart = safeGet(currData, "[0]", {});
      // FIXME: This is done for accomodating the cdc and rill pipeline
      // only cdc will have pastTrendChantId not rill based metrics
      const chart2: chart = pastData ? safeGet(pastData, "[0]", {}) : safeGet(currData, "[1]", {});

      for (let i = 0; i < chart1.values.length; i++) {
        const timekey = getRoundOffTimeKey(chart1.values[i].x, granularity);
        if (res[timekey] !== undefined) {
          res[timekey][chartLegendOffsets[timerange].current] = chart1.values[i].y;
        } else {
          res[timekey] = {
            x: timekey,
            [chartLegendOffsets[timerange].current]: chart1.values[i].y,
            formatted: formatDate(chart1.values[i].x, granularity),
          };
        }
      }

      for (let i = 0; i < safeGet(chart2, "values", []).length; i++) {
        const timeKey = getRoundOffTimeKey(chart2.values[i].x, granularity);
        if (res[timeKey] === undefined) {
          res[timeKey] = {
            x: timeKey,
            [chartLegendOffsets[timerange].past]: chart2.values[i].y,
            formatted: formatDate(chart2.values[i].x, granularity),
          };
        } else if (!res[timeKey][chartLegendOffsets[timerange].past]) {
          res[timeKey][chartLegendOffsets[timerange].past] = chart2.values[i].y;
        } else {
          res[timeKey][chartLegendOffsets[timerange].past] = chart2.values[i].y;
        }
      }

      // Parse plan orders data and merge into result
      if (safeGet(planData, "length", 0) > 0) {
        const planChart: chart = safeGet(planData, "[0]", {});
        for (let i = 0; i < safeGet(planChart, "values", []).length; i++) {
          const timeKey = getRoundOffTimeKey(planChart.values[i].x, granularity);
          if (res[timeKey] === undefined) {
            res[timeKey] = {
              x: timeKey,
              [PLAN_ORDERS_KEY]: planChart.values[i].y,
              formatted: formatDate(planChart.values[i].x, granularity),
            };
          } else {
            res[timeKey][PLAN_ORDERS_KEY] = planChart.values[i].y;
          }
        }
      }
    }

    // Only include y3Key if plan orders data exists
    const hasPlanData = safeGet(planData, "length", 0) > 0;

    const result = {
      data: Object.keys(res).map((k) => res[k]) as TrendChartData[],
      y1Key: chartLegendOffsets[timerange]?.current,
      y2Key: chartLegendOffsets[timerange]?.past,
      ...(hasPlanData && { y3Key: PLAN_ORDERS_KEY }),
    };

    return result;
  }
}
